import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

export type DeviceInfo = {
  deviceId: string;
  deviceName: string;
};

let fpPromise: ReturnType<typeof FingerprintJS.load> | null = null;

const detectBrowser = (ua: string) => {
  if (/Edg/.test(ua)) return 'Edge';
  if (/OPR|Opera/.test(ua)) return 'Opera';
  if (/Chrome/.test(ua)) return 'Chrome';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Safari/.test(ua)) return 'Safari';
  return 'Browser';
};

const detectOs = (ua: string) => {
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown OS';
};

const getWebDeviceName = () => {
  const ua = navigator.userAgent;
  return `${detectBrowser(ua)} on ${detectOs(ua)}`;
};

/**
 * Resolves a stable per-device identifier and a human-readable name for the
 * current device. Native (Capacitor) uses the OS device id + model; web falls
 * back to a FingerprintJS visitor id, which survives storage clears.
 */
export const getDeviceInfo = async (): Promise<DeviceInfo> => {
  if (Capacitor.isNativePlatform()) {
    const [{ identifier }, info] = await Promise.all([Device.getId(), Device.getInfo()]);
    const deviceName =
      info.name?.trim() || [info.manufacturer, info.model].filter(Boolean).join(' ').trim();
    return { deviceId: identifier, deviceName: deviceName || 'Mobile device' };
  }

  if (!fpPromise) fpPromise = FingerprintJS.load();
  const fp = await fpPromise;
  const { visitorId } = await fp.get();
  return { deviceId: visitorId, deviceName: getWebDeviceName() };
};
